import React, { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { format } from "date-fns";
import { currentDateFnsLocale } from "../../../i18n/dateLocales";
import {
  DocumentTextIcon,
  LockClosedIcon,
  PaperAirplaneIcon,
  CheckCircleIcon,
  ArrowsRightLeftIcon,
} from "@heroicons/react/24/outline";
import { ResourceLoading } from "../../utils/ResourceLoading";
import { ResourceError } from "../../utils/ResourceError";
import RoleOnly from "../../../routes/roleOnly";
import OfferRevisionDiff from "./offer-revision-diff";
import { useSettings } from "../../../contexts/settings-context";
import { getPdfOffer } from "../../../api/offer";
import {
  acceptOfferAsync,
  createOfferRevisionAsync,
  getOfferAsync,
  getOfferRevisionsAsync,
} from "../../../slices/offer";
import {
  isOfferFeatureEnabled,
  isOfferNotAuthorized,
  resolveOfferActionErrorMessage,
} from "./utils";

const REVISION_STATUS_CLASS = {
  draft: "bg-slate-100 text-slate-800",
  pending_review: "bg-amber-100 text-amber-900",
  approved: "bg-indigo-100 text-indigo-800",
  issued: "bg-blue-100 text-blue-800",
  sent: "bg-blue-100 text-blue-800",
  accepted: "bg-green-100 text-green-800",
  rejected: "bg-red-100 text-red-800",
  superseded: "bg-orange-100 text-orange-900",
};

function revisionStatusLabel(t, status) {
  const key = `offers.history.revision_status.${status || "draft"}`;
  const translated = t(key);
  return translated === key ? status : translated;
}

function formatRevisionDate(value) {
  if (!value) return null;
  try {
    return format(new Date(value), "d. MMM yyyy, HH:mm", { locale: currentDateFnsLocale() });
  } catch {
    return null;
  }
}

export default function OfferHistory({
  offer,
  refreshKey = 0,
  onSubmitForReview,
  onSendOffer,
  /** @deprecated use onSubmitForReview / onSendOffer */
  onSend,
}) {
  const handleSubmitForReview = onSubmitForReview || onSend;
  const handleSendOffer = onSendOffer;
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [searchParams, setSearchParams] = useSearchParams();
  const { priceFormatter } = useSettings();
  const offerFeatureFlags = useSelector(
    (state) => state.offers?.offerFeatureFlags || {}
  );
  const {
    revisions: storeRevisions,
    revisionsOfferId,
    revisionsLoading,
    revisionsError,
    acceptedRevisionId: storeAcceptedId,
  } = useSelector((state) => ({
    revisions: state.offers?.revisions || [],
    revisionsOfferId: state.offers?.revisionsOfferId,
    revisionsLoading: state.offers?.revisionsLoading,
    revisionsError: state.offers?.revisionsError,
    acceptedRevisionId: state.offers?.acceptedRevisionId,
  }));
  const revisions =
    revisionsOfferId === offer?.id ? storeRevisions : [];

  const featureFlags = offer?.offer_feature_flags || offerFeatureFlags || {};
  const revisionsEnabled = isOfferFeatureEnabled(featureFlags, "offer_revisions");
  const acceptedRevisionId =
    storeAcceptedId ?? offer?.accepted_revision_id ?? null;

  const [acceptingId, setAcceptingId] = useState(null);
  const [pdfLoadingId, setPdfLoadingId] = useState(null);
  const [creatingDraft, setCreatingDraft] = useState(false);
  const [restoringId, setRestoringId] = useState(null);
  // Activity timeline «Sammenlign revisjoner» lands with ?compare=1
  const [showCompare, setShowCompare] = useState(
    () => searchParams.get("compare") === "1"
  );

  useEffect(() => {
    if (searchParams.get("compare") === "1") {
      setShowCompare(true);
    }
  }, [searchParams]);

  const toggleCompare = () => {
    setShowCompare((prev) => {
      const next = !prev;
      const params = new URLSearchParams(searchParams);
      if (next) params.set("compare", "1");
      else params.delete("compare");
      setSearchParams(params, { replace: true });
      return next;
    });
  };

  const canCompare = revisionsEnabled && revisions.length >= 2;

  const loadRevisions = useCallback(() => {
    if (!offer?.id || !revisionsEnabled) return;
    dispatch(getOfferRevisionsAsync(offer.id));
  }, [dispatch, offer?.id, revisionsEnabled]);

  const handleEnsureDraft = async () => {
    if (!offer?.id || creatingDraft) return;
    setCreatingDraft(true);
    try {
      const lockedBase = [...revisions]
        .slice()
        .sort((a, b) => (b.revision_number || 0) - (a.revision_number || 0))
        .find(
          (r) =>
            r?.locked ||
            ["approved", "sent", "issued", "accepted", "rejected", "superseded"].includes(
              r?.status
            )
        );
      const payload = lockedBase?.id
        ? {
            offerId: offer.id,
            based_on_revision_id: lockedBase.id,
            replace_draft: true,
            change_note: t("offers.approval.reopen_note", {
              number: lockedBase.revision_number,
            }),
          }
        : { offerId: offer.id };
      const result = await dispatch(createOfferRevisionAsync(payload));
      if (createOfferRevisionAsync.rejected.match(result)) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result.payload,
            "offers.history.ensure_draft_failed"
          )
        );
        return;
      }
      toast.success(
        lockedBase
          ? t("offers.approval.reopen_success", {
              number: lockedBase.revision_number,
            })
          : t("offers.history.ensure_draft_success")
      );
      await dispatch(getOfferAsync(offer.id));
      loadRevisions();
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(t, err, "offers.history.ensure_draft_failed")
      );
    } finally {
      setCreatingDraft(false);
    }
  };

  useEffect(() => {
    loadRevisions();
  }, [loadRevisions, refreshKey, offer?.current_draft_revision_id, offer?.accepted_revision_id]);

  const handleOpenPdf = async (revision) => {
    if (!offer?.id || !revision?.id) return;
    setPdfLoadingId(revision.id);
    try {
      const response = await getPdfOffer({
        id: offer.id,
        params: { revision_id: revision.id },
      });
      if (isOfferNotAuthorized(response) || response?.error || response?.errors) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            response,
            "offers.history.pdf_failed"
          )
        );
        return;
      }
      if (response?.pdf_url) {
        window.open(response.pdf_url, "_blank", "noopener,noreferrer");
      } else {
        toast.error(t("offers.history.pdf_failed"));
      }
    } catch (err) {
      console.error("Error loading revision PDF:", err);
      toast.error(
        resolveOfferActionErrorMessage(t, err, "offers.history.pdf_failed")
      );
    } finally {
      setPdfLoadingId(null);
    }
  };

  const handleAccept = async (revision) => {
    if (!offer?.id || !revision?.id) return;
    setAcceptingId(revision.id);
    try {
      const result = await dispatch(
        acceptOfferAsync({ id: offer.id, revision_id: revision.id })
      );
      if (acceptOfferAsync.rejected.match(result)) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result.payload,
            "offers.history.accept_failed"
          )
        );
        return;
      }
      toast.success(
        t("offers.history.accept_success", {
          number: revision.revision_number,
        })
      );
      await dispatch(getOfferAsync(offer.id));
      loadRevisions();
    } catch (err) {
      console.error("Error accepting offer revision:", err);
      toast.error(
        resolveOfferActionErrorMessage(
          t,
          err,
          "offers.history.accept_failed"
        )
      );
    } finally {
      setAcceptingId(null);
    }
  };

  /** REV-001: replace draft + restore kalkyle tree from locked snapshot. */
  const handleRestoreCalculation = async (revision) => {
    if (!offer?.id || !revision?.id || restoringId) return;
    if (
      !window.confirm(
        t("offers.history.restore_calc_confirm", {
          number: revision.revision_number,
        })
      )
    ) {
      return;
    }
    setRestoringId(revision.id);
    try {
      const result = await dispatch(
        createOfferRevisionAsync({
          offerId: offer.id,
          based_on_revision_id: revision.id,
          replace_draft: true,
          change_note: t("offers.history.restore_calc_note", {
            number: revision.revision_number,
          }),
        })
      );
      if (createOfferRevisionAsync.rejected.match(result)) {
        toast.error(
          resolveOfferActionErrorMessage(
            t,
            result.payload,
            "offers.history.restore_calc_failed"
          )
        );
        return;
      }
      toast.success(
        t("offers.history.restore_calc_success", {
          number: revision.revision_number,
        })
      );
      await dispatch(getOfferAsync(offer.id));
      loadRevisions();
    } catch (err) {
      toast.error(
        resolveOfferActionErrorMessage(
          t,
          err,
          "offers.history.restore_calc_failed"
        )
      );
    } finally {
      setRestoringId(null);
    }
  };

  const canRestoreCalculation = (revision) =>
    Boolean(
      revision?.locked &&
        ["sent", "issued", "approved", "accepted", "superseded"].includes(
          revision?.status
        )
    );

  const canAcceptRevision = (revision) => {
    if (!revision?.locked && revision?.status === "draft") return false;
    if (revision?.status === "accepted") return false;
    if (acceptedRevisionId && acceptedRevisionId === revision.id) return false;
    return Boolean(revision?.locked || ["sent", "issued", "approved"].includes(revision?.status));
  };

  const hasEditableDraft = revisions.some(
    (r) =>
      r.id === offer?.current_draft_revision_id ||
      ["draft", "pending_review"].includes(r?.status)
  );
  const showReopenCta = revisionsEnabled && revisions.length > 0 && !hasEditableDraft;

  if (!revisionsEnabled) {
    return (
      <div className="bg-white shadow-sm rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="text-lg font-semibold text-gray-900">
            {t("offers.history.send_title")}
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            {t("offers.history.send_subtitle")}
          </p>
        </div>
        <div className="px-6 py-6">
          <div className="text-center py-12 text-gray-500">
            <PaperAirplaneIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
            <p>{t("offers.history.send_empty")}</p>
            <p className="text-sm mt-2">{t("offers.history.send_empty_hint")}</p>
          </div>
        </div>
      </div>
    );
  }

  if (revisionsLoading && revisions.length === 0) {
    return <ResourceLoading />;
  }

  if (revisionsError && revisions.length === 0) {
    return (
      <ResourceError
        errors={[
          isOfferNotAuthorized({ error: revisionsError })
            ? t("offers.errors.not_authorized")
            : t("offers.history.revisions_error"),
        ]}
        onRetry={loadRevisions}
      />
    );
  }

  return (
    <div className="space-y-6">
      {canCompare && showCompare && (
        <OfferRevisionDiff offer={offer} revisions={revisions} />
      )}

      <div className="bg-white shadow-sm rounded-lg">
        <div className="px-6 py-4 border-b border-gray-200 flex flex-wrap items-start justify-between gap-3">
          <div>
            <h3 className="text-lg font-semibold text-gray-900">
              {t("offers.history.revisions_title")}
            </h3>
            <p className="text-sm text-gray-500 mt-1">
              {t("offers.history.revisions_subtitle")}
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            {revisionsEnabled && revisions.length > 0 && revisions.length < 2 && (
              <p className="text-xs text-slate-500">
                {t("offers.history.compare_need_two")}
              </p>
            )}
            {canCompare && (
              <button
                type="button"
                onClick={toggleCompare}
                aria-pressed={showCompare}
                className={`inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-sm font-medium ring-1 ring-inset transition-colors ${
                  showCompare
                    ? "bg-blue-600 text-white ring-blue-600 hover:bg-blue-500"
                    : "bg-white text-blue-700 ring-blue-200 hover:bg-blue-50"
                }`}
              >
                <ArrowsRightLeftIcon className="h-4 w-4" />
                {showCompare
                  ? t("offers.diff.toggle_hide")
                  : t("offers.diff.toggle_show")}
              </button>
            )}
            {showReopenCta && (
              <RoleOnly role="offers">
                <button
                  type="button"
                  onClick={handleEnsureDraft}
                  disabled={creatingDraft}
                  className="inline-flex items-center gap-1.5 rounded-md bg-[#1463C7] px-3 py-1.5 text-sm font-semibold text-white hover:bg-[#0B3A78] disabled:opacity-50"
                >
                  {creatingDraft
                    ? t("Loading...")
                    : t("offers.approval.reopen_cta")}
                </button>
              </RoleOnly>
            )}
          </div>
        </div>
        <div className="px-6 py-4 flex flex-wrap gap-3 text-sm text-gray-700">
          <span className="rounded-md bg-slate-50 px-2 py-1 ring-1 ring-slate-200">
            {t("offers.info.draft_revision")}:{" "}
            <span className="font-medium">
              {(() => {
                const draft = revisions.find(
                  (r) => r.id === offer?.current_draft_revision_id
                );
                if (draft?.revision_number != null) {
                  return t("offers.history.revision_number", {
                    number: draft.revision_number,
                  });
                }
                return offer?.current_draft_revision_id
                  ? t("offers.history.revision_id_fallback", {
                      id: offer.current_draft_revision_id,
                    })
                  : t("offers.info.none");
              })()}
            </span>
          </span>
          <span className="rounded-md bg-slate-50 px-2 py-1 ring-1 ring-slate-200">
            {t("offers.info.accepted_revision")}:{" "}
            <span className="font-medium">
              {(() => {
                const accepted = revisions.find(
                  (r) => r.id === acceptedRevisionId
                );
                if (accepted?.revision_number != null) {
                  return t("offers.history.revision_number", {
                    number: accepted.revision_number,
                  });
                }
                return acceptedRevisionId
                  ? t("offers.history.revision_id_fallback", {
                      id: acceptedRevisionId,
                    })
                  : t("offers.info.none");
              })()}
            </span>
          </span>
        </div>

        <div className="px-6 pb-2">
          <p className="text-sm text-slate-600">
            {t("offers.history.submit_review_hint")}{" "}
            {typeof handleSubmitForReview === "function" ? (
              <button
                type="button"
                onClick={handleSubmitForReview}
                className="font-medium text-[#1463C7] hover:underline"
              >
                {t("offers.approval.send")}
              </button>
            ) : (
              <span className="font-medium text-slate-700">
                {t("offers.approval.send")}
              </span>
            )}
            {" · "}
            {t("offers.history.send_lock_hint")}{" "}
            {typeof handleSendOffer === "function" ? (
              <button
                type="button"
                onClick={handleSendOffer}
                className="font-medium text-[#1463C7] hover:underline"
              >
                {t("offers.history.send_lock_cta")}
              </button>
            ) : (
              <span className="font-medium text-slate-700">
                {t("offers.banner.send_offer")}
              </span>
            )}
            {" · "}
            {t("offers.history.approval_tab_hint")}
          </p>
        </div>

        <div className="px-6 pb-6">
          {revisions.length === 0 ? (
            <div className="text-center py-10 text-gray-500">
              <DocumentTextIcon className="h-12 w-12 mx-auto text-gray-400 mb-4" />
              <p>{t("offers.history.revisions_empty")}</p>
              <p className="text-sm mt-2">
                {t("offers.history.revisions_empty_hint")}
              </p>
              <RoleOnly role="offers">
                <button
                  type="button"
                  onClick={handleEnsureDraft}
                  disabled={creatingDraft}
                  className="mt-5 inline-flex items-center rounded-lg bg-[#1463C7] px-4 py-2.5 text-sm font-semibold text-white hover:bg-[#0B3A78] disabled:opacity-50"
                >
                  {creatingDraft
                    ? t("Loading...")
                    : t("offers.history.ensure_draft_cta")}
                </button>
              </RoleOnly>
            </div>
          ) : (
            <div className="space-y-3">
              {[...revisions]
                .slice()
                .sort(
                  (a, b) =>
                    (b.revision_number || 0) - (a.revision_number || 0)
                )
                .map((revision) => {
                  const statusClass =
                    REVISION_STATUS_CLASS[revision.status] ||
                    REVISION_STATUS_CLASS.draft;
                  const amount =
                    revision.total_ex_tax ??
                    revision.subtotal_amount ??
                    null;
                  const lockedAt = formatRevisionDate(
                    revision.locked_at || revision.sent_at
                  );
                  const submittedAt = formatRevisionDate(
                    revision.submitted_for_review_at
                  );
                  const reviewerLabel =
                    revision.reviewer?.displayTitle ||
                    revision.reviewer?.name ||
                    null;
                  const isAccepted =
                    acceptedRevisionId === revision.id ||
                    revision.status === "accepted";

                  return (
                    <div
                      key={revision.id}
                      className="border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                    >
                      <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                        <div className="flex items-start gap-3 min-w-0">
                          <div className="flex-shrink-0 w-10 h-10 bg-slate-100 rounded-full flex items-center justify-center">
                            {revision.locked ? (
                              <LockClosedIcon className="h-5 w-5 text-slate-600" />
                            ) : (
                              <DocumentTextIcon className="h-5 w-5 text-slate-600" />
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex flex-wrap items-center gap-2">
                              <h4 className="text-md font-semibold text-gray-900">
                                {t("offers.history.revision_number", {
                                  number: revision.revision_number,
                                })}
                              </h4>
                              <span
                                className={`px-2 py-0.5 rounded text-xs font-medium ${statusClass}`}
                              >
                                {revisionStatusLabel(t, revision.status)}
                              </span>
                              {revision.locked && (
                                <span className="px-2 py-0.5 rounded text-xs font-medium bg-amber-50 text-amber-900 ring-1 ring-amber-200">
                                  {t("offers.history.locked")}
                                </span>
                              )}
                              {isAccepted && (
                                <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium bg-green-50 text-green-800 ring-1 ring-green-200">
                                  <CheckCircleIcon className="h-3.5 w-3.5" />
                                  {t("offers.history.is_accepted")}
                                </span>
                              )}
                            </div>
                            <div className="mt-2 flex flex-wrap gap-x-4 gap-y-1 text-sm text-gray-600">
                              {amount != null && amount !== "" && (
                                <span>
                                  {t("offers.history.amount")}:{" "}
                                  <span className="font-medium text-gray-800">
                                    {priceFormatter?.format
                                      ? priceFormatter.format(
                                          parseFloat(amount) || 0
                                        )
                                      : amount}
                                  </span>
                                  {revision.currency
                                    ? ` ${revision.currency}`
                                    : ""}
                                </span>
                              )}
                              <span>
                                {t("offers.history.lines_count", {
                                  count: revision.lines_count ?? 0,
                                })}
                              </span>
                              {submittedAt && (
                                <span>
                                  {t("offers.history.submitted_for_review_at")}:{" "}
                                  {submittedAt}
                                </span>
                              )}
                              {reviewerLabel && (
                                <span>
                                  {t("offers.history.reviewer")}: {reviewerLabel}
                                </span>
                              )}
                              {lockedAt && (
                                <span>
                                  {t("offers.history.locked_at")}: {lockedAt}
                                </span>
                              )}
                            </div>
                            {revision.change_note ? (
                              <p className="mt-2 text-sm text-gray-500 truncate">
                                {revision.change_note}
                              </p>
                            ) : null}
                          </div>
                        </div>

                        <div className="flex flex-wrap items-center gap-2 sm:justify-end">
                          {revision.locked && (
                            <button
                              type="button"
                              onClick={() => handleOpenPdf(revision)}
                              disabled={pdfLoadingId === revision.id}
                              className="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm font-medium text-blue-700 ring-1 ring-inset ring-blue-200 hover:bg-blue-50 disabled:opacity-50"
                            >
                              {pdfLoadingId === revision.id
                                ? t("Loading...")
                                : t("offers.history.open_pdf")}
                            </button>
                          )}
                          {canRestoreCalculation(revision) && (
                            <RoleOnly role="offers">
                              <button
                                type="button"
                                onClick={() => handleRestoreCalculation(revision)}
                                disabled={restoringId === revision.id}
                                className="inline-flex items-center rounded-md bg-white px-3 py-1.5 text-sm font-medium text-slate-800 ring-1 ring-inset ring-slate-300 hover:bg-slate-50 disabled:opacity-50"
                              >
                                {restoringId === revision.id
                                  ? t("Loading...")
                                  : t("offers.history.restore_calc")}
                              </button>
                            </RoleOnly>
                          )}
                          {canAcceptRevision(revision) && (
                            <RoleOnly role="offers">
                              <button
                                type="button"
                                onClick={() => handleAccept(revision)}
                                disabled={acceptingId === revision.id}
                                className="inline-flex items-center rounded-md bg-emerald-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-emerald-500 disabled:opacity-50"
                              >
                                {acceptingId === revision.id
                                  ? t("Loading...")
                                  : t("offers.history.accept")}
                              </button>
                            </RoleOnly>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
